import { createContext, useContext } from 'react';

// What every page under /dashboard/finance needs and none of them should read
// twice: the company's payment model, and — for a Connect company — the one
// Stripe Account Session the section mints. A page that minted its own would
// re-mint on every click between sub-menus.
//
// Value shape, set by finance-section.js's FinanceSectionInner:
//   model          - CONNECT_DIRECT | LEGACY
//   isAdmin        - the viewer holds role 100. overview.js reads this to
//                    choose between the admin Finance console and the
//                    restaurant's own Overview, without a useSelector of its
//                    own (its tests render through this context with no
//                    redux store).
//   companyId
//   status         - the company's ConnectAccountStatusDto, or null before
//                    the first read / after one that found no account
//   statusLoaded   - distinct from `status`: true once a read has resolved
//                    for the current companyId, whatever it found
//   instance       - the section's one Stripe Connect instance, or null
//                    unless the account exists, the instance built, and the
//                    session did not fail (see sessionFailure)
//   sessionFailure - the section's Stripe Account Session error, if any
//   refreshStatus  - () => Promise<void>. Re-runs the same status read the
//                    section used to resolve `status`/`hasAccount` in the
//                    first place, for the page's own use when something
//                    outside this section's control changed the account —
//                    The Account page calls it once ConnectOnboarding
//                    finishes in place. A newly-true `hasAccount` flows back
//                    through this same value on the next render, including a
//                    freshly-minted `instance` once useConnectInstance's
//                    effect re-runs on the changed secretSource — no second,
//                    page-owned status read and no remount required.
//   canManagePayouts - the viewer has role 150 or 200: the roles router.js
//                    lets onto Payouts. Overview draws no way to the bank
//                    for anyone else (the accountant, 550).
//   payoutsPaused  - companySettings.payout_paused === 'true', the fact
//                    behind layout.js's section-wide banner.
//   retrySession   - () => void. Re-mints the Stripe Account Session by
//                    changing the component key useConnectInstance's build
//                    effect is keyed on. The only re-mint there is: neither
//                    hook exposes one, and a page that "retried" by reloading
//                    would throw away whatever the reader was doing.
//   firstPayoutNotice - 'legacy' | 'new' while "Your First Stripe Payout"
//                    should show, else null. Read once per company here (a
//                    non-admin on CONNECT_DIRECT only); a refused or failed
//                    read is null. Keyed on companyId, so a switch never
//                    shows the previous company's answer.
//   dismissFirstPayoutNotice - () => void. Hides the notice at once on
//                    every page and tells the API; a refused write leaves it
//                    closed for this visit.
export const FinanceSectionContext = createContext(null);

export const useFinanceSection = () => {
  const value = useContext(FinanceSectionContext);
  if (!value) {
    throw new Error('useFinanceSection must be used inside the finance section route');
  }
  return value;
};
